import React, { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";

import {
  Button,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Tooltip,
} from "@fluentui/react-components";
import {
  Add20Regular,
  ArrowClockwise20Filled,
  Delete24Regular,
  Filter20Regular,
  Search16Regular,
} from "@fluentui/react-icons";
import useOptionsStyles from "./options.styles";

import { useSelector } from "react-redux";
import { ProcessedSecurity } from "../../../../store/types";
import { selectSecurities, selectToken } from "../../../../store";

import { updateSecuritiesTable } from "../../../../../../service/sourceServices";
import { fetchData } from "../../../../store/asyncThunks";
import { useAppDispatch } from "../../../../../../store";
import PostHogService from "../../../../../../service/PostHogService";

export enum SecuritiesIdentifiers {
  ISIN = "ISIN",
  SecurityName = "Name",
  Alias = "Alias",
}

interface OptionsProps {
  onChangeSecuritiesIdentifiers: (securitiesIdentifiers: SecuritiesIdentifiers) => void;
  addDataProducers: () => void;
  deleteDataProducers: () => void;
  onSearch: (value: string) => void;
}

interface UpdateAvailableSecuritiesTableArgs {
  securities: ProcessedSecurity[];
}

async function updateAvailableSecuritiesTable({ securities }: UpdateAvailableSecuritiesTableArgs) {
  try {
    await Excel.run(async (context) => {
      await updateSecuritiesTable({ context, securities });
    });
  } catch (error) {
    Sentry.captureException(
      new Error('Error while updating available securities table', { cause: error })
    );
  }
}

const Options = ({ onChangeSecuritiesIdentifiers, addDataProducers, deleteDataProducers, onSearch }: OptionsProps) => {
  const [selectedSecuritiesIdentifiers, setSelectedSecuritiesIdentifiers] = useState<SecuritiesIdentifiers>(
    SecuritiesIdentifiers.SecurityName
  );

  const dispatch = useAppDispatch();

  const securities = useSelector(selectSecurities);
  const token = useSelector(selectToken);

  const handleUpdate = async () => {
    if (!token) return;

    await dispatch(fetchData(token));
    await updateAvailableSecuritiesTable({ securities });

    PostHogService.capture('available_securities_updated', {
      total_securities: securities.length,
      component: 'available_securities',
    });
  };

  const styles = useOptionsStyles();

  useEffect(() => {
    onChangeSecuritiesIdentifiers(selectedSecuritiesIdentifiers);
  }, [selectedSecuritiesIdentifiers]);

  return (
    <div className={styles.header}>
      <div className={styles.headerInputContainer}>
        <Input
          onChange={(event) => onSearch(event.target.value)}
          contentBefore={<Search16Regular />}
          placeholder="Find securities"
        />

        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <MenuButton icon={<Filter20Regular />} size="small">
              {selectedSecuritiesIdentifiers}
            </MenuButton>
          </MenuTrigger>

          <MenuPopover>
            <MenuList>
              <MenuItem onClick={() => setSelectedSecuritiesIdentifiers(SecuritiesIdentifiers.ISIN)}>
                {SecuritiesIdentifiers.ISIN}
              </MenuItem>
              <MenuItem onClick={() => setSelectedSecuritiesIdentifiers(SecuritiesIdentifiers.SecurityName)}>
                {SecuritiesIdentifiers.SecurityName}
              </MenuItem>
              <MenuItem onClick={() => setSelectedSecuritiesIdentifiers(SecuritiesIdentifiers.Alias)}>
                {SecuritiesIdentifiers.Alias}
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
      </div>

      <div className={styles.headerButtonsContainer}>
        <div className={styles.headerButtons}>
          <Tooltip content="Update securities" relationship="label">
            <Button size="small" onClick={handleUpdate} icon={<ArrowClockwise20Filled />} />
          </Tooltip>

          <Tooltip content="Remove selected" relationship="label">
            <Button size="small" onClick={deleteDataProducers} icon={<Delete24Regular />} />
          </Tooltip>

          <Button size="small" onClick={addDataProducers} icon={<Add20Regular />} appearance="primary">
            Add selected
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Options;
